import React, { useState } from 'react';
import { Bone, Sun } from 'lucide-react';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Repères (ANC) — Calcium : Mayo Clinic / IOM. Vitamine D : NIH Office of
// Dietary Supplements. Valeurs en mg/jour (calcium) et UI / µg par jour (vit. D).
const PROFILES = [
  { key: 'enfant-1-3', tKey: 'calcium_profile_child_1_3', calcium: 700, vitD: 600, vitDmcg: 15 },
  { key: 'enfant-4-8', tKey: 'calcium_profile_child_4_8', calcium: 1000, vitD: 600, vitDmcg: 15 },
  { key: 'ado-9-18', tKey: 'calcium_profile_teen', calcium: 1300, vitD: 600, vitDmcg: 15 },
  { key: 'adulte-19-50', tKey: 'calcium_profile_adult', calcium: 1000, vitD: 600, vitDmcg: 15 },
  { key: 'homme-51-70', tKey: 'calcium_profile_man_senior', calcium: 1000, vitD: 600, vitDmcg: 15 },
  { key: 'femme-51-70', tKey: 'calcium_profile_woman_senior', calcium: 1200, vitD: 600, vitDmcg: 15 },
  { key: 'senior-71', tKey: 'calcium_profile_elder', calcium: 1200, vitD: 800, vitDmcg: 20 },
  { key: 'grossesse-19-50', tKey: 'calcium_profile_pregnancy_adult', calcium: 1000, vitD: 600, vitDmcg: 15 },
  { key: 'grossesse-14-18', tKey: 'calcium_profile_pregnancy_teen', calcium: 1300, vitD: 600, vitDmcg: 15 },
];

const CALCIUM_SOURCES = [
  ['calcium_source_milk', '≈ 300 mg'],
  ['calcium_source_yogurt', '≈ 150 mg'],
  ['calcium_source_cheese', '≈ 250 mg'],
  ['calcium_source_sardines', '≈ 380 mg'],
  ['calcium_source_almonds', '≈ 75 mg'],
  ['calcium_source_figs', '≈ 130 mg'],
];

const CalciumVitaminD = () => {
  const { t } = useLanguage();
  const [profileKey, setProfileKey] = useState('adulte-19-50');
  const profile = PROFILES.find((p) => p.key === profileKey);

  return (
    <ToolPageLayout
      title={t('calcium_title')}
      subtitle={t('calcium_subtitle')}
      specialty="generaliste"
    >
      <div className="mb-6">
        <label className="text-sm font-medium text-[#0F172A] mb-1 block">{t('calcium_profile_label')}</label>
        <select
          value={profileKey}
          onChange={(e) => setProfileKey(e.target.value)}
          className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
        >
          {PROFILES.map((p) => (
            <option key={p.key} value={p.key}>{t(p.tKey)}</option>
          ))}
        </select>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-[#F0FDF4] border border-[#10B981]/30 rounded-2xl p-5 text-center">
          <Bone className="w-6 h-6 text-[#10B981] mx-auto mb-2" />
          <p className="text-xs text-[#065F46] mb-1">{t('calcium_daily_label')}</p>
          <p className="text-2xl font-bold text-[#10B981]">{profile.calcium} mg</p>
        </div>
        <div className="bg-[#FFFBEB] border border-[#F59E0B]/30 rounded-2xl p-5 text-center">
          <Sun className="w-6 h-6 text-[#F59E0B] mx-auto mb-2" />
          <p className="text-xs text-[#92400E] mb-1">{t('vitd_daily_label')}</p>
          <p className="text-2xl font-bold text-[#F59E0B]">{profile.vitD} UI <span className="text-base font-normal">({profile.vitDmcg} µg)</span></p>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="font-semibold text-[#0F172A] mb-3">{t('calcium_sources_heading')}</h3>
        <div className="border border-[#E2E8F0] rounded-2xl overflow-hidden">
          {CALCIUM_SOURCES.map(([foodKey, amt], i) => (
            <div key={i} className={`flex justify-between px-4 py-3 text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}`}>
              <span className="text-[#0F172A]">{t(foodKey)}</span>
              <span className="text-[#64748B] font-medium">{amt}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5">
        <p className="text-sm text-[#0F172A]">
          {t('calcium_vitd_info')}
        </p>
      </div>

      <p className="mt-6 text-sm text-[#64748B]">
        {t('calcium_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default CalciumVitaminD;
